import { Hidden } from '@mui/material'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { ExpertOverviewSection } from 'src/components/ExpertOverviewSection/ExpertOverviewSection'
import { Footer } from 'src/components/Footer/Footer'
import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { ExpertPanel } from 'src/components/global/ExpertPanel'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import { TopicCategoriesTab } from 'src/components/Tabs/TopicCategoriesTab'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IExpertFullItemFragment } from 'src/graphql/generated/hooks'
// import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption } from 'src/types'
import { getAudiencesFilter, getDateFilter } from 'src/utils/ContentFilters/contentFilters'
import { withRetries } from 'src/utils/retryHelper'

type Props = {
  expert: IExpertFullItemFragment
  contentFilters: FilterOption[]
  showTabs: boolean
}

const ExpertPage: NextPage<Props> = ({ contentFilters, expert, showTabs }: Props) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const router = useRouter()
  // const { session } = useAuthenticated()
  // const filtersDisabled = useMemo(() => Boolean(!session?.user?.isPremium), [session, expert])

  const firstName = expert.attributes?.firstName || ''
  const lastName = expert.attributes?.lastName || ''
  const slug = expert.attributes?.slug || ''
  const bio = expert.attributes?.bio || ''
  const experience = expert.attributes?.experience || ''

  const defaultText = 'No specific expertise added for this expert yet'
  const expertiseCategories = useMemo(() => {
    const expertiseSubTopics = expert.attributes?.expertiseSubTopics?.data || []
    const expertiseTopics = expert.attributes?.expertiseTopics?.data || []
    const categories: { name: string; url: string }[] = []
    expertiseTopics.forEach((topic) => {
      categories.push({
        name: topic.attributes?.name || '',
        url: `/topics/${topic.attributes?.slug}/`,
      })
    })
    expertiseSubTopics.forEach((subTopic) => {
      const topicSlug =
        subTopic.attributes?.topics?.data && subTopic.attributes?.topics?.data.length > 0
          ? subTopic.attributes?.topics?.data[0].attributes?.slug
          : null

      if (topicSlug) {
        categories.push({
          name: subTopic.attributes?.name || '',
          url: `/topics/${topicSlug}/${subTopic.attributes?.slug}/`,
        })
      }
    })
    return categories
  }, [expert])
  const fullName = `${firstName} ${lastName}`
  const description = `${fullName} ${bio}`
  const meta = <Meta title={fullName} url={`/experts/${slug}/`} description={description} />

  useEffect(() => {
    if (router.query.tab === 'profile' && tabIndex !== 0) setTabIndex(0)
    if (router.query.tab === 'content' && tabIndex !== 1) setTabIndex(1)
  }, [router])

  return (
    <ReCaptcha>
      <Main meta={meta}>
        <PremiumModal origin={`/experts/${slug}/`} isOpen={showModal} onClose={() => setShowModal(false)} />
        <MainContainer id="mainContainer">
          <div className="flex flex-col items-center bg-clouds">
            <Breadcrumb subject="MEET THE EXPERT" subtitle={fullName} />
            <ExpertOverviewSection expert={expert} />
            {showTabs && (
              <div className="!max-w-[1100px] w-full h-[50px] flex flex-row">
                <TopicCategoriesTab id={0} activeTab={tabIndex} url={`/experts/${slug}/?tab=profile`}>
                  PROFILE
                </TopicCategoriesTab>
                <TopicCategoriesTab id={1} activeTab={tabIndex} url={`/experts/${slug}/?tab=content`}>
                  CONTENT
                </TopicCategoriesTab>
              </div>
            )}
          </div>
          <div className="!max-w-[1100px] w-full mx-auto py-5 md:py-10">
            {(!showTabs || tabIndex === 0) && (
              <div className="px-5 md:pl-0 flex flex-col md:flex-row justify-between">
                <div className="md:w-3/4">
                  <ExpertPanel className="w-full mb-8" title="About" content={bio} isHtml />
                  {experience && (
                    <ExpertPanel className="w-full mb-10" title="Experience" content={experience} isHtml />
                  )}
                </div>
                <div className="md:w-[240px]">
                  <ExpertPanel
                    className="w-full"
                    title="Expertise"
                    options={expertiseCategories}
                    defaultText={defaultText}
                  />
                </div>
              </div>
            )}
            {showTabs && tabIndex === 1 && (
              <div className="flex flex-row justify-between px-5 md:px-0">
                <Hidden mdDown>
                  <div className="w-1/4 pr-10 flex flex-col">
                    <SearchField className="my-8" />
                    <Sidebar
                      filterOptions={contentFilters}
                      // filtersDisabled={filtersDisabled}
                      defaultSelectFilterIdx={1}
                      onClickWhenDisabled={() => setShowModal(true)}
                    />
                  </div>
                </Hidden>
                <ContentPagination
                  filterProps={contentFilters}
                  preAppliedFilters={{ writerIds: `expert_${expert.id}` }}
                  // filtersDisabled={filtersDisabled}
                  onClickWhenDisabled={() => setShowModal(true)}
                />
              </div>
            )}
          </div>
          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug || THEME === 'ilta') {
    return {
      revalidate: 60,
      notFound: true,
    }
  }
  try {
    const slug = params.slug as string
    // const { slugUpdates } = await apolloSdk.getSlugUpdates({ type: 'experts', oldSlug: slug })
    // if (slugUpdates && slugUpdates?.data.length > 0) {
    //   slug = slugUpdates.data[0].attributes?.newSlug || slug
    // }
    const { experts } = await withRetries(() => apolloSdk.expertBySlug({ slug }))
    if (!experts) {
      return {
        revalidate: 5,
        notFound: true,
      }
    }
    const expert = experts.data[0]
    if (!expert) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    const contentFilters: FilterOption[] = await Promise.all([getAudiencesFilter(), getDateFilter()])

    return {
      props: {
        expert,
        contentFilters,
        showTabs: (expert.attributes?.contents?.data || []).length > 0,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching expert:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { experts } = await apolloSdk.experts()
  const paths: Array<{ params: { slug: string } }> = []
  if (experts) {
    experts.data.forEach((expert) => {
      if (!expert) return
      paths.push({ params: { slug: expert.attributes?.slug || '' } })
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ExpertPage
